// APCA, the contrast model WCAG 3 is drafting. Used for one question only: of two inks, which
// reads better on this ground.
//
// WCAG 2's ratio is polarity-blind and inaccurate through the mid-tones, which is exactly where
// construction imagery lives — on a real orthomosaic it scored the two inks near-identically
// while APCA separates them clearly. The crossover falls out of the model rather than being a
// number someone picked.
//
// A draft, not a ratified standard, and nothing here claims conformance: the casing carries the
// label whichever ink wins, so this is about legibility, not clearing a floor. Ported from the
// APCA-W3 0.1.9 reference — the constants are published and not ours to tune.

export type Rgb = [number, number, number]

const TRC = 2.4
const R_CO = 0.2126729
const G_CO = 0.7151522
const B_CO = 0.072175

const BLACK_THRESHOLD = 0.022
const BLACK_CLAMP = 1.414

const NORM_BG = 0.56
const NORM_TEXT = 0.57
const REV_TEXT = 0.62
const REV_BG = 0.65

const SCALE = 1.14
const LO_OFFSET = 0.027
const LO_CLIP = 0.1
const DELTA_Y_MIN = 0.0005

// APCA's own luminance: a plain 2.4 power per channel, NOT the piecewise sRGB linearisation
// WCAG uses. They are close but not interchangeable, and mixing them silently biases the
// decision, so this deliberately does not reuse labelGroundTone's linearize.
function screenLuminance(rgb: Rgb): number {
  return (
    R_CO * Math.pow(rgb[0] / 255, TRC) +
    G_CO * Math.pow(rgb[1] / 255, TRC) +
    B_CO * Math.pow(rgb[2] / 255, TRC)
  )
}

// Very dark colours are lifted, because flare in real viewing conditions means the eye never
// sees the bottom of the range the maths allows.
function softClampBlack(y: number): number {
  return y > BLACK_THRESHOLD
    ? y
    : y + Math.pow(BLACK_THRESHOLD - y, BLACK_CLAMP)
}

// Lightness contrast, roughly -108..106. Positive is dark text on light ground, negative is
// light text on dark; the magnitude is what compares. As a rough calibration, 60 is body
// text, 45 is large text, 30 is the floor for anything meant to be read.
export function apcaLc(text: Rgb, background: Rgb): number {
  const textY = softClampBlack(screenLuminance(text))
  const bgY = softClampBlack(screenLuminance(background))

  if (Math.abs(bgY - textY) < DELTA_Y_MIN) {
    return 0
  }

  if (bgY > textY) {
    const sapc = (Math.pow(bgY, NORM_BG) - Math.pow(textY, NORM_TEXT)) * SCALE
    return sapc < LO_CLIP ? 0 : (sapc - LO_OFFSET) * 100
  }

  const sapc = (Math.pow(bgY, REV_BG) - Math.pow(textY, REV_TEXT)) * SCALE
  return sapc > -LO_CLIP ? 0 : (sapc + LO_OFFSET) * 100
}
